import { Middleware } from "redux"
import { AppDispatch, AppRootState, AppThunk } from "./appStore"
import { getLocation, LocationFix, LocationWatch, watchLocation } from "../lib/location"
import { selectImportableLocation, setLocationQuerying, setLocationResult } from "./locationSlice"
import { AppModal, unshiftAppModal } from "./appSlice"
import { updateRenderState } from "../lib/relay/updateRenderState"
import { pushLogEntry } from "./logSlice"
import { LocationStatus } from "@fusion-advantage/formrender"
import { selectAppStatusIdentities } from "../dal2/selectAppStatus"
import { syncShouldTickLocation } from "./syncthunk/SyncConfig"

// re-push the importable location to render state on this interval so the
// 15-minute hold still expires on the passage of time alone if the watch stops
// delivering fixes (e.g. no signal). This touches no GPS hardware.
const LOCATION_FORWARD_INTERVAL = 60000

// The running watch and the forward timer live at module scope, not in redux:
// the watch handle holds a non-serializable clear() closure, and neither needs
// to survive a reload. `activeWatch` also doubles as the "already watching"
// guard so we don't tear down a warm receiver on every sync.
let activeWatch: LocationWatch|null = null
let forwardTimer: ReturnType<typeof setInterval>|null = null

const stopLocationWatch = () => {
	if(activeWatch) {
		activeWatch.clear()
		activeWatch = null
	}
	if(forwardTimer) {
		clearInterval(forwardTimer)
		forwardTimer = null
	}
}

export const locationMw: Middleware<{}, AppRootState> = store => {
	const dispatch = store.dispatch as AppDispatch
	return next => action => {
		const prevState = store.getState()
		const result = next(action)
		const nextState = store.getState()
		// start location updates on sync end
		if(prevState.sync.endedAt === null && nextState.sync.endedAt !== null) {
			// skipLocation is set for docgen print jobs (headless; no one to answer a prompt)
			if(nextState.sync.config && syncShouldTickLocation(nextState.sync.config.type) && !nextState.sync.config.skipLocation) {
				// only start if we're not already watching — keep the receiver warm
				// rather than restarting the watch on every sync. If a watch is
				// already running, its forward timer keeps render state current.
				if(!activeWatch) {
					dispatch(startLocationWatch({openModalOnPrompt: true}))
				}
			}
		}
		// forward querying state to state.render for use by forms.
		// importLocation is forwarded from within the watch/forward timer instead,
		// so the 15-minute hold expiry (which is time-based, not action-based) still
		// propagates to render state independent of user actions.
		if(nextState.location.querying !== prevState.location.querying) {
			dispatch(updateRenderState({
				locationLoading: nextState.location.querying,
			}))
		}
		return result
	}
}

// push the current importable location into render state, but only when it has
// actually changed
const forwardImportLocation = (dispatch: AppDispatch, getState: () => AppRootState) => {
	const importLocation = selectImportableLocation(getState())
	if(getState().render.importLocation !== importLocation) {
		dispatch(updateRenderState({ importLocation }))
	}
}

// periodic tick fired by forwardTimer while a watch is running. Re-forwards the
// importable location (so the hold can expire on time alone) and self-stops the
// watch if the user has logged out — restoring the old poll loop's behaviour of
// tearing down within one interval of logout, so we never capture location for a
// logged-out session.
const locationForwardTick = (): AppThunk<Promise<void>> => async (dispatch, getState) => {
	const rIdentities = await selectAppStatusIdentities()
	if(rIdentities.isErr() || !rIdentities.value) {
		stopLocationWatch()
	}
	forwardImportLocation(dispatch, getState)
}

// handle one outcome streamed from the running watch: cache it, log errors, and
// forward the resulting importable location to render state.
const onLocationFix = (fix: LocationFix): AppThunk => (dispatch, getState) => {
	if(fix.isErr()) {
		// The browser watch sets a timeout, so it can re-fire the *same*
		// timeout/unavailable error every few seconds for as long as no fix is
		// obtainable. Record only a change in error status, otherwise we'd spam the
		// (unbounded) log and dispatch redundant results indefinitely. Still record
		// while `querying` so the very first error clears the loading spinner even
		// when it matches a stale prior result. Successes are never deduped: each
		// one refreshes the last-known-good timestamp, keeping the 15-min hold alive.
		const { result: prevResult, querying } = getState().location
		if(querying || !prevResult || prevResult.status !== fix.error.status) {
			dispatch(setLocationResult(fix.error))
			dispatch(pushLogEntry('[locationWatch] ' + JSON.stringify(fix.error)))
		}
		// a mid-stream Denied (browser path, where denial can't be caught up
		// front) means the user actively refused; stop watching until the next
		// qualifying sync-end re-triggers the watch.
		if(fix.error.status === LocationStatus.Denied) {
			stopLocationWatch()
		}
	} else {
		dispatch(setLocationResult(fix.value))
	}
	forwardImportLocation(dispatch, getState)
}

interface startLocationWatchArgs {
	openModalOnPrompt?: boolean
}

// (Re)establish the background location watch. Tearing down any existing watch
// first lets a manual refresh re-check permissions (e.g. right after the user
// grants access) and force a fresh acquisition cycle.
//
// Named locationMwTick for backwards compatibility with LxLocationModal, which
// calls it to refresh on demand and after the permission prompt.
export const startLocationWatch = (args?: startLocationWatchArgs): AppThunk<Promise<void>> => async (dispatch, getState) => {
	stopLocationWatch()
	// if the user has logged out, stay stopped.
	// (we must check using the local database, otherwise we can get null from the
	// redux state while it's updating from ldb after a sync)
	const rIdentities = await selectAppStatusIdentities()
	if(rIdentities.isErr() || !rIdentities.value) {
		return
	}
	dispatch(setLocationQuerying())
	const rWatch = await watchLocation(fix => dispatch(onLocationFix(fix)))
	if(rWatch.isErr()) {
		// no watch established (permission not granted, services off, etc.)
		dispatch(setLocationResult(rWatch.error))
		dispatch(pushLogEntry('[startLocationWatch] ' + JSON.stringify(rWatch.error)))
		if(rWatch.error.status === LocationStatus.Prompt && args?.openModalOnPrompt) {
			// display underneath the sync modal, if still present
			dispatch(unshiftAppModal(AppModal.Location))
		}
		forwardImportLocation(dispatch, getState)
		return
	}
	activeWatch = rWatch.value
	// keep render state current even while the watch is silent, so the hold
	// window can expire on time alone.
	forwardTimer = setInterval(() => dispatch(locationForwardTick()), LOCATION_FORWARD_INTERVAL)
	forwardImportLocation(dispatch, getState)
	// seed an immediate fix so callers that read location.result right after this
	// resolves (e.g. LxLocationModal's auto-dismiss) get a value without waiting
	// for the watch's first async callback. Only apply a success so a slow/failed
	// seed doesn't flash an error over the watch's incoming fixes.
	const rSeed = await getLocation()
	if(rSeed.isOk()) {
		dispatch(onLocationFix(rSeed))
	}
}

// backwards-compatible alias for existing callers (LxLocationModal)
export const locationMwTick = startLocationWatch
